import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { reasonLabel } from './smsReasons'

/**
 * Journal des envois SMS (`sms_journal_v1`, `sms_journal_entry_v1`). L'état affiché est calculé par le serveur ;
 * « soumis », « livré » et « livraison non confirmée » ne se confondent jamais, et « livré » n'est pas « lu ».
 */

export const journalStates = [
  'SCHEDULED',
  'PREPARED',
  'SUBMITTED',
  'DELIVERED',
  'UNCONFIRMED',
  'DELIVERY_FAILED',
  'SEND_FAILED',
  'UNCERTAIN',
  'BLOCKED',
  'CANCELLED',
  'EXPIRED',
] as const
export type JournalState = (typeof journalStates)[number]

export const sourceModules = ['SMS', 'CONTRACTS', 'BILLING', 'MISSIONS', 'OPERATOR'] as const
export type SourceModule = (typeof sourceModules)[number]

export const journalRowSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  state: z.enum(journalStates),
  outboxState: z.string(),
  reason: z.string().nullable(),
  sourceModule: z.string(),
  scope: z.string(),
  phone: z.string(),
  clientName: z.string().nullable(),
  templateName: z.string().nullable(),
  templateKey: z.string().nullable(),
  templateVersion: z.number().int().nullable(),
  authorName: z.string().nullable(),
  providerStatus: z.string().nullable(),
  errorCode: z.number().int().nullable(),
})
export type JournalRow = z.infer<typeof journalRowSchema>

export const journalPageSchema = z.object({ rows: z.array(journalRowSchema), hasMore: z.boolean() })
export type JournalPage = z.infer<typeof journalPageSchema>

const journalEventSchema = z.object({
  status: z.string(),
  errorCode: z.number().int().nullable(),
  eventType: z.string().nullable(),
  cost: z
    .object({ amount: z.string().nullable().optional(), currency: z.string().nullable().optional() })
    .nullable(),
  at: z.string(),
})

export const journalEntrySchema = journalRowSchema
  .omit({ createdAt: true, providerStatus: true, errorCode: true })
  .extend({
    createdAt: z.string(),
    updatedAt: z.string(),
    notBefore: z.string(),
    expiresAt: z.string(),
    attempts: z.number().int(),
    body: z.string(),
    senderE164: z.string().nullable(),
    message: z
      .object({
        id: z.string(),
        status: z.string(),
        provider: z.string().nullable(),
        providerSid: z.string().nullable(),
        providerStatus: z.string().nullable(),
        errorCode: z.number().int().nullable(),
        segments: z.number().int().nullable(),
        simulated: z.boolean(),
        createdAt: z.string(),
        submittedAt: z.string().nullable(),
        finalAt: z.string().nullable(),
      })
      .nullable(),
    events: z.array(journalEventSchema),
  })
export type JournalEntry = z.infer<typeof journalEntrySchema>

export interface JournalFilter {
  search?: string
  state?: JournalState | ''
  module?: SourceModule | ''
  failuresOnly?: boolean
  before?: string
}

const stateDisplay: Record<JournalState, { label: string; tone: StatusTone }> = {
  SCHEDULED: { label: 'Programmé', tone: 'neutral' },
  PREPARED: { label: 'Préparé', tone: 'neutral' },
  SUBMITTED: { label: 'Soumis · en cours', tone: 'info' },
  DELIVERED: { label: 'Livré', tone: 'success' },
  UNCONFIRMED: { label: 'Livraison non confirmée', tone: 'warning' },
  DELIVERY_FAILED: { label: 'Échec de livraison', tone: 'danger' },
  SEND_FAILED: { label: 'Échec d’envoi', tone: 'danger' },
  UNCERTAIN: { label: 'Résultat inconnu', tone: 'warning' },
  BLOCKED: { label: 'Bloqué', tone: 'neutralStrong' },
  CANCELLED: { label: 'Annulé', tone: 'neutral' },
  EXPIRED: { label: 'Expiré', tone: 'neutral' },
}

export function journalStateLabel(state: JournalState): string {
  return stateDisplay[state].label
}

export function journalStateTone(state: JournalState): StatusTone {
  return stateDisplay[state].tone
}

export const journalStateOptions = journalStates.map((state) => ({
  value: state,
  label: journalStateLabel(state),
}))

/** Ce qui demande une action : échec, livraison non confirmée, résultat de soumission inconnu. */
export function isJournalFailure(state: JournalState): boolean {
  return (
    state === 'SEND_FAILED' || state === 'DELIVERY_FAILED' || state === 'UNCONFIRMED' || state === 'UNCERTAIN'
  )
}

const moduleLabels: Record<string, string> = {
  SMS: 'Module SMS',
  CONTRACTS: 'Contrats',
  BILLING: 'Facturation',
  MISSIONS: 'Missions',
  OPERATOR: 'Opérateur',
}

export const sourceModuleOptions = sourceModules.map((module) => ({
  value: module,
  label: moduleLabels[module] ?? module,
}))

/** Origine affichée : le module SMS se détaille en Paramètres (SMS test), Réception (réponse) ou Envois. */
export function journalOriginLabel(row: Pick<JournalRow, 'sourceModule' | 'scope' | 'templateKey'>): string {
  if (row.sourceModule !== 'SMS') return moduleLabels[row.sourceModule] ?? row.sourceModule
  if (row.templateKey?.startsWith('parametres.')) return 'Paramètres'
  if (row.scope === 'CONVERSATION') return 'Réception'
  return 'Envois'
}

/** Contexte : modèle et version, ou la portée quand aucun modèle n'est rattaché. */
export function journalContextLabel(
  row: Pick<JournalRow, 'templateName' | 'templateVersion' | 'scope'>,
): string {
  if (row.templateName) {
    return row.templateVersion ? `${row.templateName} · v${String(row.templateVersion)}` : row.templateName
  }
  return scopeLabel(row.scope)
}

const scopeLabels: Record<string, string> = {
  SNOW_ALERTS: 'Alertes opérationnelles',
  ADMINISTRATIVE: 'Administratif',
  CONVERSATION: 'Réponse à un client',
  CONSENT: 'Demande d’inscription',
}

export function scopeLabel(scope: string): string {
  return scopeLabels[scope] ?? scope
}

const dateTimeFormat = new Intl.DateTimeFormat('fr-CA', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'America/Toronto',
})
const preciseFormat = new Intl.DateTimeFormat('fr-CA', {
  dateStyle: 'medium',
  timeStyle: 'medium',
  timeZone: 'America/Toronto',
})

/** Date et heure de Montréal. */
export function formatSmsDateTime(value: string | null | undefined): string {
  return value ? dateTimeFormat.format(new Date(value)) : ''
}

function formatPrecise(value: string): string {
  return preciseFormat.format(new Date(value))
}

function codeSuffix(code: number | null | undefined): string {
  return code ? ` (code ${String(code)})` : ''
}

/** Explication de l'état, sans jamais présenter un résultat inconnu comme un succès ou un échec. */
export function journalCause(entry: JournalEntry): string {
  const code = entry.message?.errorCode ?? null
  switch (entry.state) {
    case 'SCHEDULED':
      return `Programmé pour le ${formatSmsDateTime(entry.notBefore)}. Expire le ${formatSmsDateTime(entry.expiresAt)} s’il n’est pas parti.`
    case 'PREPARED':
      return 'En file d’envoi. Consentement, blocages et plafond sont revérifiés juste avant la soumission.'
    case 'SUBMITTED':
      return 'Accepté par Telnyx. En attente du résultat final de l’opérateur.'
    case 'DELIVERED':
      return 'Livré au téléphone selon l’opérateur. Les SMS n’ont pas d’accusé de lecture.'
    case 'UNCONFIRMED':
      return 'L’opérateur n’a transmis aucun résultat final. Ce n’est ni un succès ni un échec confirmé.'
    case 'DELIVERY_FAILED':
      return `L’opérateur du destinataire n’a pas livré le message${codeSuffix(code)}. Aucun réessai automatique.`
    case 'SEND_FAILED':
      return code === 40300 || entry.reason === 'PROVIDER_40300'
        ? 'Telnyx a refusé l’envoi : le destinataire a répondu STOP. Le numéro est bloqué.'
        : `Telnyx a refusé l’envoi${codeSuffix(code)}. Aucun réessai automatique.`
    case 'UNCERTAIN':
      return 'La soumission a peut-être abouti, sans réponse de Telnyx. Le message n’est pas renvoyé à l’aveugle : il attend une vérification.'
    case 'BLOCKED':
      return `Aucun SMS envoyé : ${reasonLabel(entry.reason)}.`
    case 'CANCELLED':
      return `Annulé avant la soumission à Telnyx : ${reasonLabel(entry.reason)}.`
    case 'EXPIRED':
      return 'Le délai de validité est passé avant l’envoi. Le message n’a pas été envoyé et ne sera pas rejoué.'
  }
}

const providerStatusLabels: Record<string, string> = {
  queued: 'En file chez Telnyx',
  sending: 'En cours d’envoi',
  sent: 'Envoyé à l’opérateur',
  delivered: 'Livré',
  delivery_failed: 'Échec de livraison',
  sending_failed: 'Échec d’envoi',
  delivery_unconfirmed: 'Livraison non confirmée',
}

export interface TimelineItem {
  at: string
  label: string
}

/** Chronologie réelle : intention, préparation, soumission, statuts reçus du fournisseur, issue locale. */
export function journalTimeline(entry: JournalEntry): TimelineItem[] {
  const items: { at: string; label: string; order: number }[] = []
  const add = (at: string | null | undefined, label: string, order: number) => {
    if (at) items.push({ at, label, order })
  }
  const scheduled = new Date(entry.notBefore).getTime() - new Date(entry.createdAt).getTime() > 60_000
  add(
    entry.createdAt,
    scheduled ? `Demande créée, programmée pour le ${formatSmsDateTime(entry.notBefore)}` : 'Demande créée',
    0,
  )
  const message = entry.message
  if (message) {
    add(message.createdAt, 'Contrôles revérifiés, message préparé', 1)
    add(message.submittedAt, message.simulated ? 'Envoi simulé (aucun SMS réel)' : 'Soumis à Telnyx', 2)
  }
  for (const event of entry.events) {
    const label = providerStatusLabels[event.status] ?? `Statut fournisseur : ${event.status}`
    add(event.at, `${label}${codeSuffix(event.errorCode)}`, 3)
  }
  if (entry.outboxState === 'BLOCKED')
    add(entry.updatedAt, `Refusé avant envoi : ${reasonLabel(entry.reason)}`, 4)
  if (entry.outboxState === 'CANCELLED') add(entry.updatedAt, `Annulé : ${reasonLabel(entry.reason)}`, 4)
  if (entry.outboxState === 'EXPIRED') add(entry.updatedAt, 'Expiré sans envoi', 4)
  if (entry.outboxState === 'UNCERTAIN')
    add(entry.updatedAt, 'Aucune réponse de Telnyx : résultat à vérifier', 4)
  if (entry.outboxState === 'FAILED' && entry.events.length === 0) {
    add(entry.updatedAt, `Soumission refusée par Telnyx${codeSuffix(message?.errorCode)}`, 4)
  }
  return items
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime() || a.order - b.order)
    .map(({ at, label }) => ({ at: formatPrecise(at), label }))
}

/** Coût réel transmis par Telnyx au résultat final, s'il existe. */
export function journalCost(entry: JournalEntry): string | null {
  const cost = [...entry.events].reverse().find((event) => event.cost?.amount)?.cost
  if (!cost?.amount) return null
  return `${cost.amount} ${cost.currency ?? ''}`.trim()
}
